
import Vue from "vue";
import NavigationDrawer from "@/components/vuetify/NavigationDrawer.vue";
import ElementRangeDatePicker from "@/components/element/RangeDatePicker.vue";
import FilterChipButton from "@/components/vuetify/FilterChipButton.vue";
import GroupListChart from "@/views/group-list/GroupListChart.vue";
import GroupListTable from "@/views/group-list/GroupListTable.vue";
import Loading from "@/components/vuetify/Loading.vue";
import dayjs from "dayjs";
import RadialApiClient from "@/api/RadialApiClient";
import { GroupListGraphEntity, GroupListTableItem } from "@/api/entities";
import { GroupListChartFilter, SearchEntries, SearchEntriesInSkuList } from "@/store/model";
import mixin from "@/mixin/mixin";

export interface DataType {
  isLoading: boolean;
  rangeDates: string[];
  groupBy: string;
  dataFormatIndex: number;
  tableFormat: number;
  timeUnit: string;
  tableExpanded: (string | number)[];
  graphData: GroupListGraphEntity | null;
  rawTableItems: GroupListTableItem[];
  rawTableTotal: GroupListTableItem | null;
  tableTotalCount: number;
  digestBase: string;
  selectedApiMetrics: string[];
  chartFilter: GroupListChartFilter;
  chartFilterData: GroupListChartFilter;
  sortOptionKey: string;
  isAsc: boolean;
  hoverItemId: string | number | null;
  dateRange: number;
  filters: { name: string; key: keyof GroupListChartFilter }[];
  groupBys: { name: string; key: keyof GroupListChartFilter }[];
  tableHeaders: any[];
}

const emptyChartFilter: GroupListChartFilter = {
  brand: [],
  season: [],
  category: [],
  attribute1: [],
  attribute2: [],
  attribute3: [],
  store: [],
  color: [],
  size: [],
  customTagMaster: [],
};

export default Vue.extend({
  name: "GroupList",
  mixins: [mixin],
  components: { NavigationDrawer, ElementRangeDatePicker, FilterChipButton, GroupListChart, GroupListTable, Loading },
  data(): DataType {
    return {
      isLoading: false,
      rangeDates: [dayjs().subtract(6, "day").format("YYYY-MM-DD"), dayjs().format("YYYY-MM-DD")],
      groupBy: "brand",
      dataFormatIndex: 0,
      tableFormat: 0,
      timeUnit: "day",
      tableExpanded: [],
      graphData: null,
      rawTableItems: [],
      rawTableTotal: null,
      tableTotalCount: 0,
      digestBase: "quantity",
      selectedApiMetrics: ["salesResult", "digestionRate"],
      chartFilter: { ...emptyChartFilter },
      chartFilterData: { ...emptyChartFilter },
      filters: [
        { name: "ブランド", key: "brand" },
        { name: "シーズン", key: "season" },
        { name: "アイテムカテゴリ", key: "category" },
        { name: "属性", key: "attribute1" },
        { name: "店舗", key: "store" },
        { name: "カラー", key: "color" },
        { name: "サイズ", key: "size" },
      ],
      groupBys: [
        { name: "ブランド", key: "brand" },
        { name: "シーズン", key: "season" },
        { name: "アイテムカテゴリ", key: "category" },
        { name: "属性1", key: "attribute1" },
        { name: "属性2", key: "attribute2" },
        { name: "属性3", key: "attribute3" },
        { name: "店舗", key: "store" },
        { name: "カラー", key: "color" },
        { name: "サイズ", key: "size" },
      ],
      sortOptionKey: "saleQuantity",
      isAsc: false,
      hoverItemId: null,
      dateRange: 1,
      tableHeaders: [
        { text: "名称", value: "name", align: "start", width: "150px" },
        { text: "売上 (前年比)", value: "salesResultAmount", align: "start" },
        { text: "売上シェア", value: "salesShareAmount", align: "start" },
        { text: "在庫", value: "stockAmount", align: "start" },
        { text: "消化率全体", value: "digestionRateTotal", align: "start" },
        { text: "消化率プロパー", value: "digestionRateProper", align: "start" },
        { text: "消化週数", value: "storageLife", align: "start" },
        { text: "", value: "drilldown", align: "start", width: "320px", sortable: false },
      ],
    };
  },
  computed: {
    minDate(): string {
      return dayjs().subtract(3, "year").format("YYYY-MM-DD");
    },
    dateRangeError(): boolean {
      return dayjs(this.rangeDates[1]).diff(dayjs(this.rangeDates[0]), "year") >= 1;
    },
    attributeTabTitle(): string {
      if (this.attributesLength === 1) {
        if (this.chartFilter.attribute1.length > 0) return this.chartFilter.attribute1[0].name;
        if (this.chartFilter.attribute2.length > 0) return this.chartFilter.attribute2[0].name;
        if (this.chartFilter.attribute3.length > 0) return this.chartFilter.attribute3[0].name;
      }
      return "属性";
    },
    attributesLength(): number {
      return (
        this.chartFilter.attribute1.length + this.chartFilter.attribute2.length + this.chartFilter.attribute3.length
      );
    },
    availableAttributesLength(): number {
      return (
        this.chartFilterData.attribute1.length +
        this.chartFilterData.attribute2.length +
        this.chartFilterData.attribute3.length
      );
    },
    tableDisplayItems(): any[] {
      return this.rawTableItems.map((item) => ({
        id: item.id,
        name: item.name,
        isGroup: item.isGroup,
        salesResultAmount: this.dataFormatIndex === 1 ? item.salesResultAmount : item.salesResultQuantity,
        salesResultAmountYoY: this.dataFormatIndex === 1 ? item.salesResultAmountYoY : item.salesResultQuantityYoY,
        salesShareAmount: this.dataFormatIndex === 1 ? item.salesShareAmount : item.salesShareQuantity,
        stockAmount: this.dataFormatIndex === 1 ? item.stockAmount : item.stockQuantity,
        stockAmountYoY: this.dataFormatIndex === 1 ? item.stockAmountYoY : item.stockQuantityYoY,
        digestionRateTotal: item.digestionRateTotal,
        digestionRateProper: item.digestionRateProper,
        storageLife: item.storageLife,
      }));
    },
    tableDisplayTotal(): any {
      const item = this.rawTableTotal;
      if (!item) return null;
      return {
        salesResultAmount: this.dataFormatIndex === 1 ? item.salesResultAmount : item.salesResultQuantity,
        salesResultAmountYoY: this.dataFormatIndex === 1 ? item.salesResultAmountYoY : item.salesResultQuantityYoY,
        salesShareAmount: this.dataFormatIndex === 1 ? item.salesShareAmount : item.salesShareQuantity,
        stockAmount: this.dataFormatIndex === 1 ? item.stockAmount : item.stockQuantity,
        stockAmountYoY: this.dataFormatIndex === 1 ? item.stockAmountYoY : item.stockQuantityYoY,
        digestionRateTotal: item.digestionRateTotal,
        digestionRateProper: item.digestionRateProper,
        storageLife: item.storageLife,
      };
    },
  },
  watch: {
    async timeUnit() {
      this.isLoading = true;
      try {
        await this.fetchGraphData();
      } finally {
        this.isLoading = false;
      }
    },
    rangeDates() {
      this.updateGraph();
    },
    groupBy() {
      this.updateGraph();
    },
  },
  methods: {
    onSelectGroupBy({ key, itemId }: { key: string; itemId: string | number }) {
      const filterKey = this.groupBy as keyof GroupListChartFilter;
      const filterData = this.chartFilterData[filterKey] as any[];
      const entity = filterData.find((v) => String(v.id) === String(itemId));
      if (entity) {
        (this.chartFilter[filterKey] as any[]) = [entity];
      }
      this.groupBy = key;
    },
    onShowProduct(item: any) {
      const searchEntries: SearchEntries = {
        itemNumber: "",
        brands: [],
        seasons: [],
        categories: [],
        attribute1: [],
        attribute2: [],
        attribute3: [],
        salesStatus: [],
        salesPrice: [null, null],
        stockPrice: [null, null],
        salesCount: [null, null],
        stockCount: [null, null],
        salePrice: [null, null],
        alerts: [],
        customTags: [],
        stores: [],
        statuses: [],
      };
      const entity = { id: item.id, name: item.name, code: null };
      switch (this.groupBy) {
        case "brand": searchEntries.brands = [entity]; break;
        case "season": searchEntries.seasons = [entity]; break;
        case "category": searchEntries.categories = [entity]; break;
        case "attribute1": searchEntries.attribute1 = [String(item.id)]; break;
        case "attribute2": searchEntries.attribute2 = [String(item.id)]; break;
        case "attribute3": searchEntries.attribute3 = [String(item.id)]; break;
        case "store": searchEntries.stores = [entity]; break;
      }
      const routeName = (this as any).isAdmin ? "AdminProductList" : "ProductList";
      const route = this.$router.resolve({
        name: routeName,
        query: { search: JSON.stringify(searchEntries), rangeDates: JSON.stringify(this.rangeDates) },
      });
      window.open(route.href, "_blank");
    },
    onShowSku(item: any) {
      const searchEntries: SearchEntriesInSkuList = {
        itemNumberOrJanCode: "",
        brands: [],
        seasons: [],
        categories: [],
        attribute1s: [],
        attribute2s: [],
        attribute3s: [],
        salesStatus: [],
        salesPrice: [null, null],
        stockPrice: [null, null],
        salesCount: [null, null],
        stockCount: [null, null],
        salePrice: [null, null],
        colors: [],
        sizes: [],
        alertMasters: [],
        stores: [],
        statuses: [],
      };
      const entity = { id: item.id, name: item.name, code: null };
      switch (this.groupBy) {
        case "brand": searchEntries.brands = [entity]; break;
        case "season": searchEntries.seasons = [entity]; break;
        case "category": searchEntries.categories = [entity]; break;
        case "attribute1": searchEntries.attribute1s = [entity]; break;
        case "attribute2": searchEntries.attribute2s = [entity]; break;
        case "attribute3": searchEntries.attribute3s = [entity]; break;
        case "store": searchEntries.stores = [entity]; break;
        case "color": searchEntries.colors = [{ id: item.id, name: item.name }]; break;
        case "size": searchEntries.sizes = [{ id: item.id, name: item.name }]; break;
      }
      const routeName = (this as any).isAdmin ? "AdminSkuList" : "SkuList";
      const route = this.$router.resolve({
        name: routeName,
        query: { search: JSON.stringify(searchEntries), rangeDates: JSON.stringify(this.rangeDates) },
      });
      window.open(route.href, "_blank");
    },
    async onMetricsChange(metrics: string[]) {
      this.selectedApiMetrics = metrics;
      this.isLoading = true;
      try {
        await Promise.all([this.fetchGraphData(), this.fetchTableData()]);
      } finally {
        this.isLoading = false;
      }
    },
    async onUpdateDigestBase(base: string) {
      this.digestBase = base;
      this.isLoading = true;
      try {
        await this.fetchTableData();
      } finally {
        this.isLoading = false;
      }
    },
    buildFilter() {
      return {
        groupBy: this.groupBy,
        start: dayjs(this.rangeDates[0]).toDate(),
        end: dayjs(this.rangeDates[1]).toDate(),
        brandIds: this.chartFilter.brand.map((v) => v.id as number),
        seasonIds: this.chartFilter.season.map((v) => v.id as number),
        categoryIds: this.chartFilter.category.map((v) => v.id as number),
        attribute1Ids: this.chartFilter.attribute1.map((v) => String(v.id)),
        attribute2Ids: this.chartFilter.attribute2.map((v) => String(v.id)),
        attribute3Ids: this.chartFilter.attribute3.map((v) => String(v.id)),
        storeIds: this.chartFilter.store.map((v) => String(v.id)),
        colorIds: this.chartFilter.color.map((v) => String(v.id)),
        sizeIds: this.chartFilter.size.map((v) => String(v.id)),
      };
    },
    async fetchGraphData() {
      const response = await RadialApiClient.getGroupListGraph(this.groupBy, {
        filter: this.buildFilter() as any,
        timeUnit: this.timeUnit as any,
        metrics: this.selectedApiMetrics as any,
      });
      this.graphData = response;
      this.dateRange = dayjs(this.rangeDates[1]).diff(this.rangeDates[0], "day") + 1;
    },
    async fetchTableData() {
      const response = await RadialApiClient.getGroupListTable(this.groupBy, {
        filter: this.buildFilter() as any,
        offset: 0,
        digestBase: this.digestBase as any,
        limit: 5,
        metric: this.selectedApiMetrics[0] as any,
      });
      this.rawTableItems = response?.items ?? [];
      this.rawTableTotal = response?.total ?? null;
      this.tableTotalCount = response?.totalCount ?? 0;
    },
    async onLoadMoreTable() {
      const response = await RadialApiClient.getGroupListTable(this.groupBy, {
        filter: this.buildFilter() as any,
        offset: this.rawTableItems.length,
        digestBase: this.digestBase as any,
        limit: 5,
        metric: this.selectedApiMetrics[0] as any,
      });
      if (response) {
        this.rawTableItems = [...this.rawTableItems, ...response.items];
      }
    },
    async onLoadAllTable() {
      const response = await RadialApiClient.getGroupListTable(this.groupBy, {
        filter: this.buildFilter() as any,
        offset: 0,
        digestBase: this.digestBase as any,
        limit: null,
        metric: this.selectedApiMetrics[0] as any,
      });
      this.rawTableItems = response?.items ?? [];
      this.rawTableTotal = response?.total ?? null;
    },
    async updateGraph() {
      if (this.dateRangeError) return;
      this.isLoading = true;
      try {
        await Promise.all([this.fetchGraphData(), this.fetchTableData()]);
      } finally {
        this.isLoading = false;
      }
    },
    async clearFilter() {
      this.chartFilter = JSON.parse(JSON.stringify(emptyChartFilter));
      await this.updateGraph();
    },
    clearAttributeFilter() {
      this.chartFilter.attribute1 = [];
      this.chartFilter.attribute2 = [];
      this.chartFilter.attribute3 = [];
    },
  },
  async mounted() {
    const filterInfo = await RadialApiClient.getSkuFilterInfo();
    if (filterInfo) {
      this.chartFilterData.brand = filterInfo.brands as any;
      this.chartFilterData.season = filterInfo.seasons as any;
      this.chartFilterData.category = filterInfo.categories as any;
      this.chartFilterData.attribute1 = filterInfo.attribute1 as any;
      this.chartFilterData.attribute2 = filterInfo.attribute2 as any;
      this.chartFilterData.attribute3 = filterInfo.attribute3 as any;
      this.chartFilterData.store = filterInfo.stores as any;
      this.chartFilterData.color = filterInfo.colors as any;
      this.chartFilterData.size = filterInfo.sizes as any;
    }
    await this.updateGraph();
  },
});
